<template>
  <SignInSignUpLayout>
    <b-form
      @submit="inviteTeam"
      class="sing-in-form invite-teammates-form"
    >
      <template v-if="!successfullyInvited">
        <Preloader v-if="isLoading" />

        <h1 class="sign-in-title">Invite teammates</h1>

        <Description class="invite-description">
          Add your teammates' email addresses below to send them an invitation
          and start collaborating efficiently on our platform.
        </Description>
        <div class="invitees-list">
          <div class="invitee-list-header">
            <div class="invitee-list-cell">Email</div>
            <div class="invitee-list-cell">Roles</div>
            <div class="invitee-list-cell"></div>
          </div>
          <div class="invitee-list-row" v-for="(item, index) in $v.invitees.$each.$iter" :key="index">
            <div class="invitee-list-cell">
              <b-form-group class="email-form-group" :state="getEmailState(index)">
                <b-form-input
                  class="sing-up-input"
                  v-model="item.email.$model"
                  placeholder="Enter email"
                  autocomplete="off"
                  :state="getEmailState(index)"
                />
                <div v-if="serverErrors[index] && serverErrors[index]['email']" class="d-block invalid-feedback">
                  <span v-for="(message, messageIndex) in serverErrors[index]['email']" :key="messageIndex">{{ message }}</span>
                </div>
                <template #invalid-feedback>
                  <div v-if="!item.email.isValidEmail">{{ VALIDATION_MESSAGES.email.invalid }}</div>
                  <div v-else-if="item.error">{{ item.error }}</div>
                  <div v-else-if="isDuplicateEmailIndex(index)">Email must be unique</div>
                </template>
              </b-form-group>
            </div>

            <div class="invitee-list-cell">
              <SignUpMultiselect
                :id="`role-select-${index}`"
                v-model="item.roles.$model"
                :options="_allUserRoles"
                track-by="id"
                optionLabel="display_name"
                placeholder="Select roles"
                groupClass="invitee-role-select-group"
                :state="getRoleState(index)"
                multiple
              >
                <template #invalid-feedback>
                  <div v-if="!item.roles.requiredIf">{{ VALIDATION_MESSAGES.common.required }}</div>
                </template>
              </SignUpMultiselect>
            </div>

            <div class="invitee-list-cell">
              <CloseButton
                buttonClass="delete-button"
                iconClass="delete-button-icon"
                tooltip="Remove"
                :id="`remove-row-button-${index}`"
                :onClick="() => deleteInvitee(index)"
              />
            </div>
          </div>
        </div>

        <div class="add-invitee-button" :style="{opacity: isInviteesLimit ? '0.5' : '1', cursor: isInviteesLimit ? 'not-allowed' : 'pointer' }" tabindex="0" role="button" @click="addInvitee">
          <span class="material-symbols-outlined material-symbols-fill-1 add-invitee-icon">
            add_circle
          </span>
          <span>More teammates? Click to add a row.</span>
        </div>

        <b-button class="button confirm-button" type="submit" variant="primary">
          Invite & Proceed
        </b-button>

        <slot name="progress-bar"></slot>

        <b-button class="button" variant="light" @click="finishSignupAndSignin">
          {{ skipButtonText }}
        </b-button>

        <p class="need-more-data">
        <span class="material-symbols-outlined material-symbols-fill-1 info-icon">
          info
        </span>
          Need more data to get started? You can always request us to import your data later.
        </p>
      </template>

      <SuccessView v-else title="Invitations sent successfully">
        <p>
          You will be redirected to your
          <a :href="authorizeAndRedirectToHomeURL" @click="finishSignupAndSignin">agency dashboard</a>
          in {{ redirectToAgencyDashboardTimeout }} seconds.
        </p>
      </SuccessView>
    </b-form>
  </SignInSignUpLayout>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { requiredIf, email } from 'vuelidate/lib/validators'
import { COMPLETE_SIGN_UP_USER_SPECIFIED_COMPANY_SIZE } from '@/constants/localStorageKeys'
import SignInSignUpLayout from '@components/sign-up/SignInSignUpLayout.vue'
import Description from '@components/sign-up/Description.vue'
import SignUpMultiselect from '@components/sign-up/SignUpMultiselect.vue'
import CloseButton from '@components/common/CloseButton.vue'
import Preloader from '@components/Preloader.vue'
import SuccessView from '@components/sign-up/SuccessView.vue'

const emptyInvitee = {
  email: null,
  roles: null
}

const DEFAULT_INVITEE_LIMIT = 9

export default {
  name: 'InviteTeammatesForm',
  mixins: [validationMixin],
  components: {
    SuccessView,
    Preloader,
    SignInSignUpLayout,
    Description,
    SignUpMultiselect,
    CloseButton
  },
  data () {
    let userSpecifiedCompanySize = localStorage.getItem(COMPLETE_SIGN_UP_USER_SPECIFIED_COMPANY_SIZE)

    let inviteesLimit = DEFAULT_INVITEE_LIMIT

    if (userSpecifiedCompanySize) {
      userSpecifiedCompanySize = parseInt(userSpecifiedCompanySize)
      if (userSpecifiedCompanySize) {
        inviteesLimit = userSpecifiedCompanySize - 1 // remove current user from the count
      }
    }

    return {
      invitees: [
        { ...emptyInvitee },
        { ...emptyInvitee },
        { ...emptyInvitee }
      ],
      inviteesLimit,

      skipButtonText: 'Skip for now',

      isLoading: false,
      submitButtonPressed: false,
      successfullyInvited: false,
      redirectToAgencyDashboardTimeout: 5,
      authorizeAndRedirectToHomeURL: '/api/register/redirect-to-tenant',
      serverErrors: [],
      isInviteesLimit: false,
      formIndexToServerIndex: {}
    }
  },
  computed: {
    duplicateEmails () {
      const emails = this.invitees.map(inv => inv.email?.trim().toLowerCase())
      const duplicates = new Set()
      const seen = new Map()

      emails.forEach((email, i) => {
        if (!email) return
        if (seen.has(email)) {
          duplicates.add(i)
          duplicates.add(seen.get(email))
        } else {
          seen.set(email, i)
        }
      })
      return duplicates
    },
    self () {
      return this
    }
  },
  validations () {
    return {
      invitees: {
        $each: {
          email: {
            isValidEmail: (value) => {
              if (value) {
                return email(value)
              } else {
                return true
              }
            },
            uniqueEmail () {
              return true
            }
          },
          roles: {
            requiredIf: requiredIf((invitee) => {
              return Boolean(invitee.email)
            })
          }
        }
      }
    }
  },
  async created () {
    this.successfullyInvited = this.isTestingE2E ? this.$route.query?.successfullyInvited === 'true' : false

    this.isLoading = true
    await this.$api.roles.getRoles()
      .catch(error => {
        console.error(error)
        this.$snotify.error(error, 'Error', this.$store.state.snotify)
      })
      .finally(() => {
        this.isLoading = false
      })
  },
  watch: {
    'invitees.length' (newValue) {
      if (newValue >= this.inviteesLimit) {
        this.isInviteesLimit = true
      } else {
        this.isInviteesLimit = false
      }
    }
  },
  methods: {
    isDuplicateEmailIndex (index) {
      return this.duplicateEmails.has(Number(index))
    },
    getEmailState (index) {
      return this.submitButtonPressed && (this.$v.invitees.$each[index].email.$invalid || this.isDuplicateEmailIndex(index)) ? false : null
    },
    getRoleState (index) {
      return this.submitButtonPressed && this.$v.invitees.$each[index].roles.$invalid ? false : null
    },
    inviteTeam (e) {
      e.preventDefault()

      this.submitButtonPressed = true

      this.$v.$touch()
      if (this.$v.$invalid || Array.from(this.duplicateEmails).length) {
        return
      }

      // Create mapping between original form indices and filtered array indices
      const formIndexToServerIndex = {}
      let serverIndex = 0

      const userInvitations = this.invitees.reduce((acc, invitee, formIndex) => {
        if (invitee.email && invitee.roles) {
          formIndexToServerIndex[serverIndex] = formIndex
          acc.push({
            email: invitee.email,
            roles: invitee.roles.map(role => role.id)
          })
          serverIndex++
        }

        return acc
      }, [])

      // Store the mapping for error handling
      this.formIndexToServerIndex = formIndexToServerIndex

      if (!userInvitations.length) {
        this.$snotify.info(
          `Use "${this.skipButtonText}" to proceed without inviting.`,
          'No teammates were added',
          { ...this.$store.state.snotify }
        )
        return
      }

      this.isLoading = true

      this.axios
        .post('/api/register/invite-users', { user_invitations: userInvitations })
        .then(async () => {
          this.successfullyInvited = true
          setTimeout(() => {
            this.finishSignupAndSignin()
          }, this.redirectToAgencyDashboardTimeout * 1000)
        })
        .catch((error) => {
          if (error.response?.data?.errors) {
            this.serverErrors = this.mapInvitationErrorsByIndex(error.response.data.errors, this.formIndexToServerIndex)
            // this.errorNotifications(error)
          } else {
            console.error(error)
            this.$snotify.error(error, 'Error', this.$store.state.snotify)
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    addInvitee () {
      if (this.invitees.length < this.inviteesLimit) {
        this.invitees.push({ ...emptyInvitee })
      }
    },
    deleteInvitee (index) {
      this.invitees.splice(index, 1)
    },
    finishSignupAndSignin (e) {
      if (e && e.preventDefault) {
        e.preventDefault()
      }
      localStorage.removeItem(COMPLETE_SIGN_UP_USER_SPECIFIED_COMPANY_SIZE)
      window.location.href = this.authorizeAndRedirectToHomeURL
    },
    mapInvitationErrorsByIndex (rawErrors, formIndexToServerIndex) {
      const result = {}
      for (const key in rawErrors) {
        const match = key.match(/^user_invitations\.(\d+)\.([\w-]+)$/)
        if (match) {
          const [, serverIndex, field] = match
          // Map server index back to original form index
          const formIndex = formIndexToServerIndex[serverIndex]

          if (formIndex !== undefined) {
            if (!result[formIndex]) result[formIndex] = {}
            result[formIndex][field] = rawErrors[key]
          }
        }
      }
      return result
    }
  }
}
</script>

<style scoped lang="scss">
.sign-in-title {
  line-height: 48px !important;
  margin-bottom: 10px !important;
}

.invite-description {
  margin-bottom: 27px;
}

.invitee-list-header {
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 500;
  line-height: 20.68px;
}

.invitee-list-header, .invitee-list-row {
  display: flex;
}

.invitee-list-row {
  margin-bottom: 30px;
}

$first-cell-width: 51%;

.invitee-list-cell:nth-child(1) {
  margin-right: 10px;
  width: $first-cell-width;
}

.email-form-group {
  margin-bottom: 0 !important;
}

.invitee-list-cell:nth-child(2) {
  width: calc(100% - $first-cell-width);
}

.invitee-list-cell:nth-child(3) {
  width: 0;
  display: flex;
  align-items: center;
}

.invitee-role-select-group {
  margin-bottom: 0 !important;
}

.invite-teammates-form {
  ::v-deep .delete-button {
    margin-top: 4px;
    margin-left: 6px;
    color: $fontColorBase;

    &:hover, &:focus {
      color: lighten($fontColorBase, 15%);
    }
  }

  ::v-deep .delete-button-icon {
    font-size: 23px;
    font-weight: 500;
  }
}

/* Add invitee button */
.add-invitee-button {
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 10px;
}

.add-invitee-icon {
  color: $fontColorAccent;
  font-size: 30px;
}

/* Need more data block */
.need-more-data {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 30px;
  line-height: 18.38px;
}

.info-icon {
  position: relative;
  bottom: 2px;
  font-size: 28px;
  color: $fontColor2;
}
</style>
